import { useGetFunctionsDetailQuery, useUpdateFunctionMutation } from 'store/services/helios/functionsService';
import { useGetDatasourcesQuery } from 'store/services/helios/dataSources';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, FormProvider, useWatch, useFieldArray } from 'react-hook-form';
import { Box, Grid, MenuItem, FormHelperText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';

import type { UpdateFunctionReq, FunctionType, FunctionWorkflowCommentType, FunctionParameter, FuncParameterType } from 'types/functions';
import { TextField } from 'components/mui/common/TextField';
import { Switch } from 'components/mui/common/Switch';
import { ActionModal } from 'components/mui/common/ActionModal';
import { useEditFunctionModalSchema } from 'utils/validations/helios/useHeliosFunctionsSchema';
import { Select } from 'components/mui/common/Select';
import { Autocomplete, type Option } from 'components/mui/common/Autocomplete';
import { ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH, AddParametersToFunctionModal } from './AddParametersToFunctionModal';
import type { AddParametersModalSchema, FunctionParamsFormValues } from './AddParametersToFunctionModal';
import { EDIT_FUNCTION_MODAL_TRANSLATE_PATH } from './EditFunctionModal.constants';
import type { EditFunctionModalProps, EditFunctionModalSchema } from './EditFunctionModal.types';
import { LoadingContainer } from 'components/common/LoadingContainer';
import { Button } from 'components/mui/common/Button';
import { useModals } from 'utils/hooks';
import { translateValues, TranslationField } from 'components/mui/common/TranslationField';
import { BooleanWrapper, ChipsWrapper, FormWrapper, ParameterWrapper, SectionTitle, StyledChip } from './FunctionModals.styles';

const isOption = (option: unknown): option is Option => typeof option === 'object' && option !== null && 'label' in option && 'value' in option;

const mapFunctionParameterToSchema = (param: FunctionParameter): AddParametersModalSchema => ({
  id: param.id,
  name: param.name,
  labelText: param.labelText,
  functionParameterType: param.functionParameterType,
  dateFormatVariant: param.dateFormatVariant,
  order: param.order,
  isRequired: param.isRequired,
  isMultiSelect: param.isMultiSelect,
  functionParameterSelectedList: param.functionParameterSelectedList,
  templateBrowseId: param.templateBrowseId || null,
});

const allFuncParameterTypes: FuncParameterType[] = [
  'Text',
  'Relation',
  'Checkbox',
  'Date',
  'Time',
  'DateTime',
  'Radio',
  'Password',
  'File',
  'Unknown',
  'MultiSelect',
];

const isFuncParameterType = (value: string | undefined): value is FuncParameterType =>
  !!value && allFuncParameterTypes.some((type) => type === value);

const isDateFormatVariant = (value: string | null | undefined): value is 'Date' | 'DateTime' | 'Time' | 'MonthYear' =>
  value === 'Date' || value === 'DateTime' || value === 'Time' || value === 'MonthYear';

const allFunctionTypeValues: FunctionType[] = ['Db', 'Heg', 'HegWorkflow'];

const isFunctionType = (value: string | undefined): value is FunctionType => !!value && allFunctionTypeValues.some((type) => type === value);

const allFunctionWorkflowCommentTypes: FunctionWorkflowCommentType[] = ['None', 'Optional', 'Required'];

const isFunctionWorkflowCommentType = (value: string | undefined): value is FunctionWorkflowCommentType =>
  !!value && allFunctionWorkflowCommentTypes.some((type) => type === value);

const mapSchemaToFunctionParameterType = (schema: AddParametersModalSchema): UpdateFunctionReq['functionParameters'][number] => ({
  id: schema.id ?? undefined,
  name: schema.name ?? '',
  labelText: schema.labelText ?? '',
  functionParameterType: isFuncParameterType(schema.functionParameterType) ? schema.functionParameterType : 'Text',
  dateFormatVariant: isDateFormatVariant(schema.dateFormatVariant) ? schema.dateFormatVariant : null,
  order: schema.order ?? 0,
  isRequired: schema.isRequired ?? false,
  isMultiSelect: schema.isMultiSelect ?? false,
  templateBrowseId: schema.templateBrowseId ?? undefined,
  functionParameterSelectedList: (schema.functionParameterSelectedList || []).map((item) => ({
    name: item.name,
    value: item.value ?? '',
  })),
});

const buildUpdateFunctionRequest = (
  values: EditFunctionModalSchema,
  functionId: string | null,
  datasourceName: string | undefined
): UpdateFunctionReq => ({
  id: functionId ?? '',
  name: values.name ?? '',
  column: values.column ?? '',
  number: values.number ?? 0,
  functionType: isFunctionType(values.functionType) ? values.functionType : 'Db',
  heliosFunctionId: values.heliosFunctionId ?? 0,
  datasourceId: values.datasourceId,
  datasourceName: datasourceName ?? '',
  parentColumn: values.parentColumn ?? '',
  translatesName: (values.translatesName ?? []).map((item) => ({
    language: item.language ?? '',
    value: item.value ?? '',
  })),
  isCommon: values.isCommon ?? false,
  skipRecordsOnError: values.skipRecordsOnError ?? false,
  entryFunction: values.entryFunction ?? false,
  functionParameters: (values.functionParameters ?? []).map(mapSchemaToFunctionParameterType),
  functionWorkflowCommentType: isFunctionWorkflowCommentType(values.functionWorkflowCommentType) ? values.functionWorkflowCommentType : 'None',
});

const queryDefaultValues = {
  page: 1,
  pageSize: 100,
  orderBy: 'name' as const,
  orderDesc: false,
  search: '',
  Name: '',
  WsUrlBasic: '',
  WsUrlDomain: '',
  WsUrlGoogle: '',
  WsProfile: '',
  TimeZoneId: '',
};

const defaultValues = {
  name: '',
  column: '',
  functionType: 'Db',
  heliosFunctionId: 1,
  isCommon: false,
  skipRecordsOnError: false,
  entryFunction: false,
  parentColumn: '',
  datasourceId: '',
  datasourceName: '',
  translatesName: translateValues,
  functionParameters: [],
  functionWorkflowCommentType: 'None',
};

export const EditFunctionModal: React.FC<EditFunctionModalProps> = ({ open, onClose, functionId }) => {
  const { t } = useTranslation();
  const [modals, openModal, closeModal] = useModals({
    addParametersModal: false,
    translationModal: false,
  });

  const [editingParameterIndex, setEditingParameterIndex] = useState<number | null>(null);
  const { data: editData, isLoading: isFunctionDetailLoading } = useGetFunctionsDetailQuery({ functionId: functionId ?? '' }, { skip: !functionId });
  const { data: dataSourceData, isLoading: isDataSourceLoading } = useGetDatasourcesQuery(queryDefaultValues);
  const [updateFunction] = useUpdateFunctionMutation();

  const editFunctionModalSchema = useEditFunctionModalSchema();

  const methods = useForm<EditFunctionModalSchema>({
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(editFunctionModalSchema),
  });

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { isSubmitting },
  } = methods;
  const [entryFunction, functionType] = useWatch({
    control,
    name: ['entryFunction', 'functionType'],
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'functionParameters',
  });

  const functionTypes = useMemo(
    () => [
      { value: 'Heg', name: t(`helios.functions.addFunctionModal.fields.functionTypes.Heg`) },
      { value: 'HegWorkflow', name: t(`helios.functions.addFunctionModal.fields.functionTypes.HegWorkflow`) },
    ],
    [t]
  );

  const functionWorkflowCommentTypes = [
    { value: 'None', name: t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.worflowCommentType.none`) },
    { value: 'Optional', name: t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.worflowCommentType.optional`) },
    { value: 'Required', name: t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.worflowCommentType.required`) },
  ];

  const handleAddParameter = (parameter: AddParametersModalSchema) => {
    if (editingParameterIndex !== null) {
      setValue(`functionParameters.${editingParameterIndex}`, parameter);
      setEditingParameterIndex(null);
    } else {
      append(parameter);
    }
    closeModal('addParametersModal')();
  };

  const handleEditParameter = (index: number) => {
    setEditingParameterIndex(index);
    const parameterToEdit = getValues(`functionParameters.${index}`) ?? null;
    setValue('currentParameter', parameterToEdit);
    openModal('addParametersModal')();
  };

  const handleClose = useCallback(() => {
    onClose();
    reset(defaultValues);
    setEditingParameterIndex(null);
  }, [onClose, reset]);

  const onSubmit = async (values: EditFunctionModalSchema) => {
    const selectedDataSource = dataSourceData?.items?.find((item) => item.id === values.datasourceId);
    const data = buildUpdateFunctionRequest(values, functionId, selectedDataSource?.name);

    try {
      const res = await updateFunction(data);
      if (!('error' in res)) {
        toast.success(t('helios.functions.toasts.success.edit'));
        handleClose();
      } else {
        toast.error(t('helios.functions.toasts.errors.edit'));
      }
    } catch (_error) {
      toast.error(t('helios.functions.toasts.errors.edit'));
    }
  };

  const initialValues = useMemo(() => {
    if (!editData) return defaultValues;

    const functionParams = editData.functionParameterList?.map(mapFunctionParameterToSchema) ?? [];

    return {
      ...editData,
      functionParameters: functionParams,
    };
  }, [editData]);

  useEffect(() => {
    if (editData) {
      reset(initialValues);
    }
  }, [functionId, editData, reset, initialValues]);

  const datasourceId = getValues('datasourceId');

  const dataSourceOptions = useMemo(
    () =>
      dataSourceData?.items?.map((option) => ({
        value: option.id,
        label: option.name,
      })) ?? [],
    [dataSourceData]
  );

  return (
    <ActionModal
      open={open}
      title={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.title`)}
      onClose={handleClose}
      onCancel={handleClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      disableBackdropClick={isSubmitting}
      cancelText={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.buttons.cancel`)}
      submitText={t(`${ADD_FUNCTION_PARAMETERS_MODAL_TRANSLATE_PATH}.buttons.update`)}
      submitIcon={{
        icon: <AddIcon color="inherit" />,
        position: 'start',
      }}>
      <FormProvider {...methods}>
        <LoadingContainer loading={isFunctionDetailLoading}>
          <FormWrapper>
            <SectionTitle variant="caption">{t('helios.functions.addFunctionModal.fields.sections.basicInfo')}</SectionTitle>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <LoadingContainer loading={isDataSourceLoading}>
                <Controller
                  name="datasourceId"
                  control={control}
                  disabled={isDataSourceLoading}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      {...field}
                      options={dataSourceOptions}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      label={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.dataSource`)}
                      placeholder={t('common.select')}
                      onChange={(val) => field.onChange(isOption(val) ? val.value : '')}
                      value={field.value}
                      disableCloseOnSelect={false}
                    />
                  )}
                />
              </LoadingContainer>
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error } }) => (
                  <TranslationField
                    label={t('common.name')}
                    {...field}
                    error={Boolean(error)}
                    helperText={error?.message}
                    fullWidth
                    background="white"
                    translatesName="translatesName"
                  />
                )}
              />
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Grid container spacing={2}>
                <Grid size={6}>
                  <Controller
                    name="column"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        fullWidth
                        label={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.column`)}
                      />
                    )}
                  />
                </Grid>
                {entryFunction && (
                  <Grid size={6}>
                    <Controller
                      name="parentColumn"
                      control={control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          fullWidth
                          label={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.parentColumn`)}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
            <Controller
              control={control}
              name="functionType"
              render={({ field, fieldState }) => (
                <Select
                  {...field}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  fullWidth
                  label={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.type`)}>
                  {functionTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            <Controller
              name="heliosFunctionId"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="number"
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  fullWidth
                  label={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.id`)}
                />
              )}
            />

            <SectionTitle variant="caption">{t('helios.functions.addFunctionModal.fields.sections.parameters')}</SectionTitle>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Button
                startIcon={<AddIcon />}
                color="secondary"
                variant="contained"
                onClick={openModal('addParametersModal')}
                sx={{ width: 'fit-content' }}>
                {t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.buttons.addParameters`)}
              </Button>
            </Box>

            {fields.length > 0 && (
              <ChipsWrapper>
                {fields.map((parameter, index) => {
                  const fieldError = methods.formState.errors.functionParameters?.[index];
                  const hasError = Boolean(fieldError);
                  return (
                    <ParameterWrapper key={parameter.id || index}>
                      <StyledChip label={parameter.name} onDelete={() => remove(index)} onClick={() => handleEditParameter(index)} />
                      <FormHelperText error={hasError} variant="filled">
                        {fieldError?.order?.message ?? ''}
                      </FormHelperText>
                    </ParameterWrapper>
                  );
                })}
              </ChipsWrapper>
            )}

            <SectionTitle variant="caption">{t('helios.functions.addFunctionModal.fields.sections.settings')}</SectionTitle>

            <BooleanWrapper>
              <Controller
                name="isCommon"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={(_e, checked) => field.onChange(checked)}
                    slotProps={{ input: { ref: field.ref } }}
                    name={field.name}
                    label={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.isCommon`)}
                  />
                )}
              />
              <Controller
                name="skipRecordsOnError"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={(_e, checked) => field.onChange(checked)}
                    slotProps={{ input: { ref: field.ref } }}
                    name={field.name}
                    label={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.skipEntriesWithError`)}
                  />
                )}
              />
              <Controller
                name="entryFunction"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onChange={(_e, checked) => field.onChange(checked)}
                    slotProps={{ input: { ref: field.ref } }}
                    name={field.name}
                    label={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.isEntryFunction`)}
                  />
                )}
              />
            </BooleanWrapper>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Grid container spacing={2}>
                {functionType === 'HegWorkflow' && (
                  <Grid size={6}>
                    <Controller
                      name="functionWorkflowCommentType"
                      control={control}
                      render={({ field, fieldState }) => (
                        <Select
                          {...field}
                          error={fieldState.invalid}
                          helperText={fieldState.error?.message}
                          fullWidth
                          label={t(`${EDIT_FUNCTION_MODAL_TRANSLATE_PATH}.fields.functionWorkflowCommentType`)}>
                          {functionWorkflowCommentTypes.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </FormWrapper>
        </LoadingContainer>
        <AddParametersToFunctionModal
          open={modals.addParametersModal}
          datasourceId={datasourceId || undefined}
          onSubmit={handleAddParameter}
          onClose={() => {
            closeModal('addParametersModal')();
            setEditingParameterIndex(null);
          }}
          editingParameter={
            editingParameterIndex !== null ? (getValues(`functionParameters.${editingParameterIndex}`) as FunctionParamsFormValues) : undefined
          }
        />
      </FormProvider>
    </ActionModal>
  );
};
